import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { GoogleHealthService } from '../../services/googleHealthService';

/**
 * Landing page for a researcher-sent, one-time Google Health migration link.
 * The invitation token is deliberately carried in the URL fragment so it is
 * never sent in a browser request for this page or placed in ordinary access
 * logs. It is removed from browser history before the POST request begins.
 */
const GoogleHealthReauthorization: React.FC = () => {
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
    window.history.replaceState(null, document.title, window.location.pathname);
    if (!token) {
      setError('This reauthorization link is incomplete. Please ask the study team for a new link.');
      return;
    }

    GoogleHealthService.consumeReauthorizationInvitation(token)
      .then((response) => {
        if (!response.success || !response.authorizationUrl) {
          throw new Error('The authorization service did not return a Google sign-in URL.');
        }
        window.location.assign(response.authorizationUrl);
      })
      .catch((requestError: unknown) => {
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'This reauthorization link is invalid, expired, or has already been used.',
        );
      });
  }, []);

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          {error ? (
            <>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Reauthorization unavailable
              </Typography>
              <Alert severity="error">{error}</Alert>
            </>
          ) : (
            <>
              <CircularProgress sx={{ mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 2 }}>
                Continue to Google Health
              </Typography>
              <Typography color="text.secondary">
                You are being securely redirected to approve the StudySync health-data connection.
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default GoogleHealthReauthorization;
